/**
 * richText.ts — helpers for entity descriptions that may contain HTML.
 *
 * US (RIDB) facility descriptions arrive as raw HTML fragments
 * ("<h2>Overview</h2><p>Devils Garden Campground is located…</p>") while AU
 * descriptions are plain text. These helpers let render sites keep the
 * plain-text path byte-identical and safely render the HTML ones:
 *
 *   - looksLikeHtml(s)            cheap probe — does this contain markup?
 *   - sanitizeDescriptionHtml(s)  allowlist-only sanitizer for body copy
 *   - stripHtml(s)                tags removed + entities decoded — for
 *                                 metadata, JSON-LD and card snippets
 *
 * Deliberately dependency-free and DOM-free: these run in server components,
 * client components and jest's node environment alike.
 */

/** Tags kept (attribute-less) by sanitizeDescriptionHtml. Everything else is stripped. */
const ALLOWED_TAGS = new Set([
  'h2', 'h3', 'h4', 'p', 'br', 'ul', 'ol', 'li', 'strong', 'b', 'em', 'i',
]);

/**
 * A tag-shaped token: `<name …>` or `</name …>`, with quoted attribute values
 * respected so `title="a>b"` doesn't terminate the tag early. No whitespace is
 * allowed after `<` (matching the HTML5 parser), so prose like "sites 5 < 12"
 * is NOT treated as markup.
 */
const TAG_TOKEN_SOURCE = String.raw`<(\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^"'>])*)>`;

/** Probe form of the above (non-global so .test() is stateless). */
const TAG_PROBE = new RegExp(TAG_TOKEN_SOURCE);

/** script/style/iframe blocks — removed INCLUDING their content (tolerates a missing close tag). */
const DROP_WITH_CONTENT = /<(script|style|iframe)\b(?:"[^"]*"|'[^']*'|[^"'>])*>[\s\S]*?(?:<\/\1\s*>|$)/gi;

/** HTML comments (tolerates an unterminated trailing comment). */
const COMMENTS = /<!--[\s\S]*?(?:-->|$)/g;

/** Decode the entities that actually occur in feed text. `&amp;` is decoded
 *  LAST so double-escaped input ("&amp;lt;") yields the literal "&lt;", not "<". */
function decodeBasicEntities(s: string): string {
  const fromCode = (m: string, code: number) =>
    Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : m;
  return s
    .replace(/&#(\d+);/g, (m, d) => fromCode(m, Number(d)))
    .replace(/&#x([0-9a-fA-F]+);/g, (m, h) => fromCode(m, parseInt(h, 16)))
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&amp;/gi, '&');
}

/**
 * Cheap test for markup presence. Plain-text descriptions containing stray
 * angle brackets ("sites 5 < 12") do NOT match — only tag-shaped input does.
 */
export function looksLikeHtml(s: string | null | undefined): boolean {
  if (!s) return false;
  return TAG_PROBE.test(s);
}

/**
 * Remove ALL markup and decode basic entities, collapsing whitespace.
 * Use for metadata descriptions, JSON-LD and short card snippets.
 * Plain text passes through unchanged (modulo whitespace collapse/trim).
 */
export function stripHtml(input: string | null | undefined): string {
  if (!input) return '';
  let s = String(input)
    .replace(COMMENTS, ' ')
    .replace(DROP_WITH_CONTENT, ' ')
    // Every remaining tag becomes a space so "</p><p>" keeps a word boundary.
    .replace(new RegExp(TAG_TOKEN_SOURCE, 'g'), ' ');
  s = decodeBasicEntities(s);
  return s.replace(/\s+/g, ' ').trim();
}

// Private placeholder characters — swapped in for kept tags so stray `<`/`>`
// can be escaped without touching them, then swapped back.
const OPEN_MARK = String.fromCharCode(1);
const CLOSE_MARK = String.fromCharCode(2);
const MARKS_RE = new RegExp('[' + OPEN_MARK + CLOSE_MARK + ']', 'g');
const OPEN_MARK_RE = new RegExp(OPEN_MARK, 'g');
const CLOSE_MARK_RE = new RegExp(CLOSE_MARK, 'g');

/**
 * Allowlist-only sanitizer for description body copy rendered via
 * dangerouslySetInnerHTML.
 *
 *   - keeps h2/h3/h4/p/br/ul/ol/li/strong/b/em/i, with ALL attributes stripped
 *   - removes script/style/iframe including their content
 *   - removes any other tag but keeps its inner text (e.g. <a href>x</a> → x)
 *   - escapes stray `<` / `>` that aren't part of a tag
 *
 * Existing character entities (&amp;, &#8211;, …) are left intact — the
 * browser decodes them on render.
 */
export function sanitizeDescriptionHtml(input: string | null | undefined): string {
  if (!input) return '';
  let s = String(input)
    // Paranoia: our placeholder control chars must never come from input.
    .replace(MARKS_RE, '')
    .replace(COMMENTS, '')
    .replace(DROP_WITH_CONTENT, '');

  // Swap tag tokens for placeholders (allowlisted, attribute-less) or drop them.
  s = s.replace(new RegExp(TAG_TOKEN_SOURCE, 'g'), (_whole, slash: string, rawName: string) => {
    const name = rawName.toLowerCase();
    if (!ALLOWED_TAGS.has(name)) return '';
    if (name === 'br') return `${OPEN_MARK}br /${CLOSE_MARK}`;
    return `${OPEN_MARK}${slash ? '/' : ''}${name}${CLOSE_MARK}`;
  });

  // Escape anything angle-bracket-shaped that survived (stray/malformed markup)…
  s = s.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // …then restore the tags we kept.
  s = s.replace(OPEN_MARK_RE, '<').replace(CLOSE_MARK_RE, '>');

  return s.trim();
}
